import React from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle } from '@mui/material';
import { isEmpty } from 'lodash';
import type { MouseEvent } from 'react';
import { createScopedI18n } from '../i18n/i18n';
import { LoadingButton, LoadingButtonProps } from './loading_button';

const t = createScopedI18n('components.confirm_loading_button');

export type ConfirmLoadingButtonProps = LoadingButtonProps & {
  dialogTitle?: string;
  dialogContent?: string;
  confirmButton?: string;
  dismissButton?: string;
  dialogProps?: DialogProps;
};

const ConfirmLoadingButton = ({
  onClick,
  dialogTitle = t('dialog_title'),
  dialogContent = t('dialog_content'),
  confirmButton = t('confirm_button'),
  dismissButton = t('dismiss_button'),
  dialogProps,
  ...props
}: ConfirmLoadingButtonProps) => {
  const [open, setOpen] = React.useState(false);

  const handleButtonClick = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleConfirm = React.useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setOpen(false);
      onClick?.(event);
    },
    [onClick],
  );

  const handleDismiss = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <LoadingButton onClick={handleButtonClick} {...props} />

      <Dialog open={open} onClose={handleDismiss} fullWidth maxWidth="xs" {...dialogProps}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        {isEmpty(dialogContent) ? null : <DialogContent>{dialogContent}</DialogContent>}
        <DialogActions>
          <Button variant="outlined" color="error" onClick={handleDismiss}>
            {dismissButton}
          </Button>
          <Button variant="contained" color="primary" disableElevation onClick={handleConfirm}>
            {confirmButton}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfirmLoadingButton;
